import { createAction } from '@reduxjs/toolkit';
import type { UnusualOperation } from 'commons/modules/service/unusual-operations';
import type { RootState } from './configureStore';
import { createReportSlice } from './reportStore';

// Initial filter keys for Unusual Operations report
const initialFilters = {
  client: '',
  alertType: '',
  issuer: '',
  institution: '',
};

// Custom actions for multi-selection with exclusion pattern
export const toggleSelectedId = createAction<number>(
  'operationUnusual/toggleSelectedId',
);

export const toggleSelectAll = createAction('operationUnusual/toggleSelectAll');

export const clearSelectedIds = createAction(
  'operationUnusual/clearSelectedIds',
);

// Create generic slice instance for this report with custom extensions
const slice = createReportSlice<UnusualOperation>(
  'operationUnusual',
  initialFilters,
  (builder) => {
    // Handle toggleSelectedId - toggle individual item in selection
    builder.addCase(toggleSelectedId, (state, action) => {
      const id = action.payload;

      const toggleInArray = (arr: number[], itemId: number): number[] =>
        arr.includes(itemId)
          ? arr.filter((x) => x !== itemId)
          : [...arr, itemId];

      if (state.isAllSelected) {
        state.excludedIds = toggleInArray(state.excludedIds || [], id);
      } else {
        state.selectedIds = toggleInArray(state.selectedIds || [], id);
      }
    });

    // Handle toggleSelectAll - toggle between select all and select none
    builder.addCase(toggleSelectAll, (state) => {
      state.isAllSelected = !state.isAllSelected;
      state.selectedIds = [];
      state.excludedIds = [];
    });

    // Handle clearSelectedIds - clear all selections
    builder.addCase(clearSelectedIds, (state) => {
      state.isAllSelected = false;
      state.selectedIds = [];
      state.excludedIds = [];
    });
  },
);

// Custom reducer wrapper to initialize selection fields
const baseReducer = slice.reducer;
export default (state: any, action: any) => {
  // Initialize selection fields if not present
  if (!state) {
    return baseReducer(
      {
        ...slice.getInitialState(),
        isAllSelected: false,
        selectedIds: [],
        excludedIds: [],
      },
      action,
    );
  }
  return baseReducer(state, action);
};

// Custom selectors for complex selection logic
export const getUnusualData = (state: RootState) =>
  state.entities.operationUnusual.data;

export const getSelectedIds = (state: RootState) =>
  state.entities.operationUnusual.selectedIds || [];

export const getExcludedIds = (state: RootState) =>
  state.entities.operationUnusual.excludedIds || [];

export const getIsAllSelected = (state: RootState): boolean => {
  const unusual = state.entities.operationUnusual;
  const {
    isAllSelected,
    excludedIds = [],
    selectedIds = [],
    total,
    data,
  } = unusual;

  const hasData = data?.length > 0 || total > 0;
  if (!hasData) return false;

  if (isAllSelected) {
    return excludedIds.length === 0;
  }

  return selectedIds.length === total;
};

export const getIsIndeterminate = (state: RootState) => {
  const unusual = state.entities.operationUnusual;
  const {
    data = [],
    isAllSelected,
    selectedIds = [],
    excludedIds = [],
  } = unusual;

  if (data.length === 0) return false;

  const isSelected = (id: number) =>
    isAllSelected ? !excludedIds.includes(id) : selectedIds.includes(id);

  const someSelected = data.some((item) => isSelected(item.id));
  const allSelected = data.every((item) => isSelected(item.id));

  return someSelected && !allSelected;
};

export const getHasSelectedRecords = (state: RootState) => {
  const unusual = state.entities.operationUnusual;
  const { isAllSelected, selectedIds = [] } = unusual;

  if (isAllSelected) return true;

  return selectedIds.length > 0;
};
